<script>
import { mapActions, mapGetters } from 'vuex'
import { pickBy, get, capitalize } from 'lodash'
import api from '@/api'
import BlogCreatePost from './BlogCreatePost'

export default {
  name: 'blog-edit-post',
  extends: BlogCreatePost,
  props: {
    postId: {
      required: true,
      type: String
    }
  },
  async created () {
    if (!this.blog) {
      await this.fetchBlog(this.blogId)
    }
    if (!this.post) {
      await this.fetchPost({
        blogUuid: this.blog.uuid,
        postUuid: this.postId
      })
    }
    this.currentLanguage = this.post.language
    this.currentTitle = this.post.title
    this.currentSubtitle = this.post.subtitle
    this.currentAuthor = this.post.author
    this.currentText = this.post.text
    this.category = this.post.category
    this.isPublished = this.post.isPublished
    if (this.headerImageObj) {
      this.currentHeaderImgSrc = this.headerImageObj.image
    }
    if (this.footerImagesObjList) {
      this.currentFooterImageSrcList = this.footerImagesObjList.map(obj => obj.image)
    }
    if (this.footerVideosObjList) {
      this.currentFooterVideoSrcList = this.footerVideosObjList.map(obj => obj.video)
    }
  },
  data () {
    return {
      pageTitle: this.$t('post.edit'),
      submitButtonTxt: this.$t('buttonConfirm')
    }
  },
  computed: {
    ...mapGetters({
      getPost: 'entities/posts/getPost'
    }),
    post () {
      return this.getPost(this.postId)
    },
    tabs () {
      if (!this.blog || !this.post) return []
      return this.blog.blogInfo.map(obj => ({
        title: capitalize(obj.language),
        value: obj.language,
        disabled: obj.language !== this.post.language
      }))
    },
    dbForm () {
      return {
        title: this.currentTitle,
        subtitle: this.currentSubtitle,
        author: this.currentAuthor,
        text: this.currentText,
        categoryId: this.categoryId,
        isPublished: this.isPublished
      }
    },
    changedForm () {
      if (!this.post) return
      return pickBy(this.dbForm, (val, key) => val !== this.post[key])
    },
    isFormChanged () {
      if (!this.changedForm) return
      return !!Object.keys(this.changedForm).length
    },
    headerImageObj () {
      if (!this.post) return
      const imgObj = this.post.images.find(obj => obj.position === 1)
      if (!imgObj) return
      return imgObj
    },
    footerImagesObjList () {
      if (!this.post) return
      return this.post.images
        .filter(obj => obj.position === 2)
        .sort((a, b) => a.order - b.order)
    },
    footerVideosObjList () {
      if (!this.post) return
      return this.post.videos
        .filter(obj => obj.position === 2)
        .sort((a, b) => a.order - b.order)
    },
    isHeaderImageChanged () {
      const postImgSrc = this.headerImageObj ? this.headerImageObj.image : ''
      return postImgSrc !== this.currentHeaderImgSrc
    },
    isFooterImagesChanged () {
      if (this.footerImagesObjList.length !== this.currentFooterImageSrcList.length) {
        return true
      }
      return !this.footerImagesObjList.every((obj, i) => {
        return obj.image === this.currentFooterImageSrcList[i]
      })
    },
    isFooterVideosChanged () {
      if (this.footerVideosObjList.length !== this.currentFooterVideoSrcList.length) {
        return true
      }
      return !this.footerVideosObjList.every((obj, i) => {
        return obj.video === this.currentFooterVideoSrcList[i]
      })
    },
    isEdited () {
      return this.isFormChanged || this.isHeaderImageChanged || this.isFooterImagesChanged || this.isFooterVideosChanged
    },
    isButtonDisabled () {
      return !this.isEdited
    }
  },
  methods: {
    ...mapActions({
      editPost: 'entities/posts/editPost',
      fetchPost: 'entities/posts/fetchPost'
    }),
    onFooterVideosInput (fileList) {
      for (const file of fileList) {
        const blobURL = URL.createObjectURL(file)
        this.currentFooterVideoSrcList.push(blobURL)
        // this.currentFooterVideoFilesList.push(file)
        this.currentFooterVideoFilesList[this.currentFooterVideoSrcList.length - 1] = file
      }
    },
    async editMedia () {
      const postUuid = this.post.uuid
      const reqList = []
      if (this.isHeaderImageChanged) {
        if (!this.currentHeaderImgSrc) {
          if (this.headerImageObj) {
            reqList.push(
              api.blogs.deletePostImage({
                postUuid,
                imgId: this.headerImageObj.id
              })
            )
          }
        } else {
          const image = await this.getFileFromSrc(this.currentHeaderImgSrc)
          if (this.headerImageObj) {
            reqList.push(
              api.blogs.editPostImage({
                postUuid,
                imgId: this.headerImageObj.id
              }, {
                position: 1,
                order: 1,
                image
              })
            )
          } else {
            reqList.push(
              api.blogs.savePostImage(postUuid, {
                position: 1,
                order: 1,
                image
              })
            )
          }
        }
      }
      if (this.isFooterImagesChanged) {
        this.footerImagesObjList.forEach(async (obj, index) => {
          const newImageSrc = this.currentFooterImageSrcList[index]
          if (!newImageSrc) {
            reqList.push(
              api.blogs.deletePostImage({
                postUuid,
                imgId: obj.id
              })
            )
          } else if (newImageSrc !== obj.image) {
            const image = await this.getFileFromSrc(newImageSrc)
            reqList.push(
              api.blogs.editPostImage({
                postUuid,
                imgId: obj.id
              }, {
                image
              })
            )
          }
        })
        if (this.currentFooterImageSrcList.length > this.footerImagesObjList.length) {
          for (let i = this.footerImagesObjList.length; i < this.currentFooterImageSrcList.length; i++) {
            const image = await this.getFileFromSrc(this.currentFooterImageSrcList[i])
            reqList.push(
              api.blogs.savePostImage(postUuid, {
                position: 2,
                order: i + 1,
                image
              })
            )
          }
        }
      }
      if (this.isFooterVideosChanged) {
        this.footerVideosObjList.forEach(async (obj, index) => {
          const newVideoSrc = this.currentFooterVideoSrcList[index]
          if (!newVideoSrc) {
            reqList.push(
              api.blogs.deletePostVideo({
                postUuid,
                videoId: obj.id
              })
            )
          } else if (newVideoSrc !== obj.video) {
            const video = this.currentFooterVideoFilesList[index]
            if (video) {
              reqList.push(
                api.blogs.editPostVideo({
                  postUuid,
                  videoId: obj.id
                }, {
                  video
                })
              )
            }
          }
        })
        if (this.currentFooterVideoSrcList.length > this.footerVideosObjList.length) {
          for (let i = this.footerVideosObjList.length; i < this.currentFooterVideoSrcList.length; i++) {
            const video = this.currentFooterVideoFilesList[i]
            if (video) {
              reqList.push(
                api.blogs.savePostVideo(postUuid, {
                  position: 2,
                  order: i + 1,
                  video
                })
              )
            }
          }
        }
      }
      return Promise.all(reqList)
    },
    async onSubmit () {
      this.$v.$touch()
      if (!this.isEdited || this.loading || this.$v.$invalid) return
      this.loading = true
      try {
        if (this.isFormChanged) {
          await this.editPost({
            blogUuid: this.blog.uuid,
            postUuid: this.post.uuid,
            data: this.changedForm
          })
        }
        if (this.isHeaderImageChanged || this.isFooterImagesChanged || this.isFooterVideosChanged) {
          await this.editMedia()
        }
        await this.$router.push({
          name: 'app-blogs-post',
          params: {
            blogId: this.blogId,
            postId: this.post.uuid
          }
        })
      } catch (e) {
        const error = get(e, 'response.data')
        this.$modal.show('error', { error })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style module lang="scss">
.container {
  padding: 10px 25px;
  overflow-x: hidden;
  overflow-y: scroll;
  .button {
    margin: 0 auto;
  }
}
.tabs:global(.tabs) {
  margin-bottom: 12px;
  :global(.tabs__item) {
    flex-grow: unset;
    flex-basis: auto;
  }
}
.title {
  font-size: 18px;
  font-weight: 500;
  color: $text-success;
  margin: 0;
  margin-bottom: 16px;
  text-align: center;
}
.fileIcon {
  margin-right: 8px;
  color: $text-success;
}
.footerImages {
  display: flex;
  flex-wrap: wrap;
}
.footerImage {
  margin-right: 10px;
  margin-bottom: 10px;
}
</style>
